import { FileSource, UploadedFile, UploadedFiles } from '@/domain/file'
import { QueryParamsService } from '@/service/query-params.service'
import { RepaymentsService } from '@/service/repayments.service'
import { UploadedFileService } from '@/service/uploaded-file.service'
import { OutboundSyncsService } from '@/service/outbound-syncs.service'
import { CommonModule } from '@angular/common'
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import {
  ActivatedRoute,
  NavigationStart,
  ParamMap,
  Router,
} from '@angular/router'
import { AccordionModule } from 'primeng/accordion'
import { ConfirmationService, MessageService } from 'primeng/api'
import { BadgeModule } from 'primeng/badge'
import { ButtonModule } from 'primeng/button'
import { ConfirmDialogModule } from 'primeng/confirmdialog'
import { IconFieldModule } from 'primeng/iconfield'
import { InputIconModule } from 'primeng/inputicon'
import { InputTextModule } from 'primeng/inputtext'
import { SkeletonModule } from 'primeng/skeleton'
import { SelectModule } from 'primeng/select'
import { ColumnFilter, Table, TableModule } from 'primeng/table'
import { ToastModule } from 'primeng/toast'
import { TooltipModule } from 'primeng/tooltip'
import { Subject } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { UploadedFileRowComponent } from './uploaded-file-row/recent-uploads.component'
import { OperationState } from '../../shared/operation-state'

const DEFAULT_SORT_ORDER = -1
const DEFAULT_SORT_FIELD = 'date'
const FILE_DELETION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

@Component({
  selector: 'recent-uploads',
  templateUrl: 'recent-uploads.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    BadgeModule,
    ButtonModule,
    InputTextModule,
    InputIconModule,
    IconFieldModule,
    FormsModule,
    AccordionModule,
    TooltipModule,
    ToastModule,
    SkeletonModule,
    SelectModule,
    UploadedFileRowComponent,
    ConfirmDialogModule,
  ],
  providers: [MessageService],
  styleUrls: ['recent-uploads.component.scss'],
})
export class RecentUploadsComponent implements OnInit, OnDestroy {
  @ViewChildren(ColumnFilter) columnFilters!: QueryList<ColumnFilter>
  expandedRow: string | null = null
  expandedFile: UploadedFile = null
  country: string | null = null
  public uploadedFiles: UploadedFiles = []

  selectedFiles!: UploadedFile[]

  statuses!: any[]
  searchValue!: string
  loading: boolean = true

  sortField: string = DEFAULT_SORT_FIELD
  sortOrder: number = DEFAULT_SORT_ORDER

  activityValues: number[] = [0, 100]

  hasActiveFilters: boolean = false
  private readonly committingFileIds = new Set<string>()
  private readonly committingFileProgress = new Map<string, number>()
  private readonly deletingFileProgress = new Map<string, number>()
  public readonly operationState = new OperationState()
  readonly sourceOptions = [
    { label: FileSource.ApsCashplus, value: FileSource.ApsCashplus },
    { label: FileSource.Cardnet, value: FileSource.Cardnet },
    { label: FileSource.Cardone, value: FileSource.Cardone },
    { label: FileSource.Gocardless, value: FileSource.Gocardless },
    { label: FileSource.DojoDirectSplit, value: FileSource.DojoDirectSplit },
    { label: FileSource.Modulr, value: FileSource.Modulr },
    { label: FileSource.Fdms, value: FileSource.Fdms },
  ]

  private readonly destroy$ = new Subject<void>()
  private readonly lifecycleAbort = new AbortController()
  private readonly timeouts = new Set<ReturnType<typeof setTimeout>>()
  private commitProgressPoll: ReturnType<typeof setInterval> | null = null
  private destroyed = false
  private fileLoadGeneration = 0

  constructor(
    private uploadedFileService: UploadedFileService,
    private repaymentsService: RepaymentsService,
    private changeDetector: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService,
    private readonly queryParamsService: QueryParamsService,
    private readonly messageService: MessageService,
    private readonly outboundSyncsService: OutboundSyncsService,
  ) {}

  ngOnInit() {
    this.commitProgressPoll = setInterval(() => {
      void this.refreshCommitProgress()
    }, 1500)
    this.uploadedFileService.files$
      .pipe(takeUntil(this.destroy$))
      .subscribe((files) => {
        if (this.destroyed) {
          return
        }
        this.uploadedFiles = this.sortUploadedFiles([...(files || [])])
        void this.refreshCommitProgress()
        if (this.loading && this.uploadedFiles.length > 0) {
          this.loading = false
        }
        this.scheduleTask(() => this.changeDetector.detectChanges())
      })
    this.outboundSyncsService.uploadedFilesChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.destroyed) return
        this.loadFilesForCurrentView()
      })
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.closeAllColumnFilters())
    this.queryParamsService.queryParamMap$
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        if (this.destroyed) {
          return
        }
        this.uploadedFiles = this.uploadedFileService.files || []
        this.loading = !(this.uploadedFiles?.length > 0)

        this.loadFilesForCurrentView()

        this.country = params.get('country')
      })
  }

  getSeverity(status: string) {
    const normalized = status?.toUpperCase() || ''
    if (normalized.startsWith('ERROR')) return 'danger'
    switch (normalized) {
      case 'PROCESSING':
        return 'info'
      case 'UNSAVED':
        return 'warn'

      case 'SAVED':
        return 'success'

      default:
        return null
    }
  }

  clear(table: Table) {
    this.searchValue = ''
    if (this.sortField !== DEFAULT_SORT_FIELD) {
      this.sortField = DEFAULT_SORT_FIELD
    }

    if (this.sortOrder !== DEFAULT_SORT_ORDER) {
      this.sortOrder = DEFAULT_SORT_ORDER
    }

    if (this.hasActiveFilters) {
      Object.keys(table.filters).forEach((key) => {
        table.filters[key] = [
          { matchMode: 'startsWith', operator: 'and', value: null },
        ]
      })

      table._filter() // Re-apply the empty filters
      this.sortField = DEFAULT_SORT_FIELD
      this.sortOrder = DEFAULT_SORT_ORDER
    }
    this.uploadedFiles = this.sortUploadedFiles([...(this.uploadedFiles || [])])
  }

  onFilter(event: any) {
    this.hasActiveFilters = Object.values(event.filters).some(
      (filterArray: any[]) =>
        Array.isArray(filterArray) &&
        filterArray.some(
          (f) => f.value !== null && f.value !== undefined && f.value !== '',
        ),
    )
  }

  onTableSort(event: { field: string; order: number }) {
    if (event.field === 'merchant') this.sortField = 'merchants(name)'
    else if (event.field === 'terminal') this.sortField = 'terminals(name)'
    else this.sortField = event.field
    this.sortOrder = event.order
    this.uploadedFiles = this.sortUploadedFiles([...(this.uploadedFiles || [])])
  }

  canDeleteFile(file: UploadedFile, now = Date.now()): boolean {
    const fileDate = new Date(file.date).getTime()

    // Keep files with an unavailable date actionable; only a known date older
    // than seven days is restricted.
    return Number.isNaN(fileDate) || now - fileDate <= FILE_DELETION_WINDOW_MS
  }

  deleteFile(event: Event, file: UploadedFile) {
    console.log('Deleting file', event)
    if (
      this.destroyed ||
      !this.canDeleteFile(file) ||
      this.isCommittingFile(file)
    ) {
      return
    }
    const fileID = file.id
    if (!fileID) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'This will remove all associated repayments.',
      header: 'Delete File',
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Delete',
        severity: 'danger',
      },
      accept: () => {
        if (this.destroyed || this.isCommittingFile(file)) {
          return
        }
        if (!this.operationState.begin(`file:${fileID}:delete`)) {
          return
        }
        this.deletingFileProgress.set(fileID, 0)
        this.uploadedFileService
          .deleteFiles([fileID], {
            signal: this.lifecycleAbort.signal,
            onProgress: (completed, total) => {
              this.deletingFileProgress.set(
                fileID,
                total ? Math.floor((completed / total) * 100) : 0,
              )
            },
          })
          .then(() => {
            this.uploadedFileService
              .loadAllFiles({ signal: this.lifecycleAbort.signal })
              .catch((error) => this.swallowAbort(error))
          })
          .catch((error) => this.swallowAbort(error))
          .finally(() => {
            this.deletingFileProgress.delete(fileID)
            this.operationState.end(`file:${fileID}:delete`)
          })
      },
      reject: () => {},
    })
  }

  onFileClick(file: UploadedFile) {
    this.router.navigate(['repayments'], {
      queryParams: {
        sourceFileId: file.id,
        merchants: null,
        terminals: null,
        cashAdvances: null,
        showAll: false,
      },
      queryParamsHandling: 'merge', // keep existing query params
    })
    // this.expandedRow = this.expandedRow ? '' : file.id
  }

  downloadFile(event: Event, file: UploadedFile) {
    if (this.destroyed) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    const key = `file:${file.id ?? file.name}:download`
    if (!this.operationState.begin(key)) {
      return
    }
    this.uploadedFileService
      .getSignedDownloadURL(file.download_url || file.name, {
        signal: this.lifecycleAbort.signal,
      }, file.id)
      .then((url) => {
        if (this.destroyed) {
          return
        }
        const a = document.createElement('a')
        a.href = url
        a.download = url.split('/').pop() || 'file'
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
      })
      .catch((error) => this.swallowAbort(error))
      .finally(() => this.operationState.end(key))
  }

  isDownloadingFile(file: UploadedFile): boolean {
    return this.operationState.isPending(`file:${file.id ?? file.name}:download`)
  }

  isStartingFileDeletion(file: UploadedFile): boolean {
    return !!file.id && this.operationState.isPending(`file:${file.id}:delete`)
  }

  async saveFile(event: Event, file: UploadedFile) {
    if (this.destroyed) {
      return
    }
    event.preventDefault()
    event.stopPropagation()
    if (
      !file.id ||
      this.committingFileIds.has(file.id) ||
      this.isFileSaved(file)
    ) {
      return
    }
    this.committingFileIds.add(file.id)
    this.committingFileProgress.set(file.id, 0)
    try {
      const validation = await this.repaymentsService.validateStagedRepayments(
        file.id!,
        {
          signal: this.lifecycleAbort.signal,
          requireMerchant: file.source !== FileSource.Custom,
        },
      )
      if (!validation.valid) {
        await this.uploadedFileService.updateFile(
          { ...file, status: 'ERROR' },
          { signal: this.lifecycleAbort.signal },
        )
        this.messageService.add({
          severity: 'error',
          summary: 'Cannot commit file',
          detail: `Fix the required fields before committing. ${this.formatValidationErrors(validation.errors)}`,
          life: 8000,
        })
        return
      }
      await this.repaymentsService.stageRepayments(file.id!, {
        signal: this.lifecycleAbort.signal,
        onQueued: () => this.setFileCommitStatus(file.id!, 'COMMITTING'),
        onProgress: (completed, total) => {
          this.committingFileProgress.set(
            file.id!,
            total ? Math.floor((completed / total) * 100) : 0,
          )
        },
      })
      await this.uploadedFileService.updateFile(
        { ...file, status: 'PROCESSED' },
        { signal: this.lifecycleAbort.signal },
      )
      this.messageService.add({
        severity: 'success',
        summary: 'File committed',
        detail: 'The file has been committed to Salesforce.',
        life: 4000,
      })
      await this.uploadedFileService.loadAllFiles({
        signal: this.lifecycleAbort.signal,
      })
    } catch (error) {
      if (!this.isAbortError(error)) {
        // A request may fail after earlier batches have committed. Mark the
        // upload visibly failed and tell the user how to return to a known
        // consistent state instead of encouraging an unsafe retry.
        this.uploadedFileService
          .updateFile(
            { ...file, status: 'ERROR' },
            { signal: this.lifecycleAbort.signal },
          )
          .catch(() => undefined)
        this.messageService.add({
          severity: 'error',
          summary: 'File commit incomplete',
          detail:
            'Some repayments may have been committed. Delete this file, wait for deletion to finish, then upload the file again before retrying.',
          life: 12000,
        })
      }
    } finally {
      this.committingFileIds.delete(file.id)
      this.committingFileProgress.delete(file.id)
    }
  }

  isCommittingFile(file: UploadedFile): boolean {
    return !!file.id && (this.committingFileIds.has(file.id) || file.status?.toUpperCase() === 'COMMITTING')
  }

  commitProgressPercent(file: UploadedFile): number {
    return file.id ? (this.committingFileProgress.get(file.id) ?? 0) : 0
  }

  private setFileCommitStatus(fileID: string, status: 'COMMITTING'): void {
    this.uploadedFiles = this.uploadedFiles.map((uploadedFile) =>
      uploadedFile.id === fileID ? { ...uploadedFile, status } : uploadedFile,
    )
    this.uploadedFileService.updateUploadedFileCache(
      this.uploadedFiles.filter((uploadedFile) => uploadedFile.id === fileID),
    )
    this.changeDetector.markForCheck()
  }

  private async refreshCommitProgress(): Promise<void> {
    if (this.destroyed) return
    const committingIDs = this.uploadedFiles
      .filter((file) => file.id && file.status?.toUpperCase() === 'COMMITTING')
      .map((file) => file.id!)
    if (committingIDs.length === 0) return

    try {
      const jobs = await this.repaymentsService.getActiveFileCommitJobs(
        committingIDs,
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed) return
      for (const job of jobs) {
        this.committingFileProgress.set(
          job.file_id,
          job.total_rows > 0
            ? Math.floor((job.committed_rows / job.total_rows) * 100)
            : 0,
        )
      }
      // A finished job can disappear from the active-job RPC between polls.
      // Refresh the file status so its action and percentage are removed.
      if (jobs.length !== committingIDs.length) {
        await this.uploadedFileService.loadAllFiles({
          signal: this.lifecycleAbort.signal,
        })
      }
      this.changeDetector.markForCheck()
    } catch (error) {
      this.swallowAbort(error)
    }
  }

  isFileSaved(file: UploadedFile): boolean {
    return file.status?.toUpperCase() === 'PROCESSED'
  }

  isDeletingFile(file: UploadedFile): boolean {
    return !!file.id && this.deletingFileProgress.has(file.id)
  }

  deleteProgressPercent(file: UploadedFile): number {
    return file.id ? (this.deletingFileProgress.get(file.id) ?? 0) : 0
  }

  private formatValidationErrors(errors: string[]): string {
    const visible = errors.slice(0, 5).join('; ')
    return errors.length > 5 ? `${visible}; and ${errors.length - 5} more.` : visible
  }

  private isAbortError(error: unknown): boolean {
    return (
      !!error &&
      typeof error === 'object' &&
      'name' in error &&
      (error as { name?: string }).name === 'AbortError'
    )
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.destroy$.next()
    this.destroy$.complete()
    this.closeAllColumnFilters()
    this.lifecycleAbort.abort()
    if (this.commitProgressPoll) clearInterval(this.commitProgressPoll)
    this.clearScheduledTasks()
  }

  private swallowAbort(error: unknown) {
    if (this.isAbortError(error)) {
      return
    }

    this.messageService.add({
      severity: 'error',
      summary: 'Request failed',
      detail: error instanceof Error ? error.message : 'Please try again.',
      life: 7000,
    })
  }

  private loadFilesForCurrentView(): void {
    const generation = ++this.fileLoadGeneration
    this.uploadedFileService
      .loadAllFiles({ signal: this.lifecycleAbort.signal })
      .then(() => {
        // A newer navigation may already have replaced this request. Only the
        // most recent completed request is allowed to settle the loading UI.
        if (this.destroyed || generation !== this.fileLoadGeneration) {
          return
        }
        this.updateFileCache(this.uploadedFileService.files)
        this.uploadedFiles = this.sortUploadedFiles([...(this.uploadedFiles || [])])
        this.loading = false
      })
      .catch((error) => this.swallowAbort(error))
  }

  private closeAllColumnFilters(): void {
    const hideFilters = () =>
      this.columnFilters?.forEach((filter) => {
        try {
          filter.hide()
        } catch {
          // ignore
        }
      })

    hideFilters()
    // Fallback: broadcast ESC to close any stray overlays
    setTimeout(() => {
      hideFilters()
      document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }))
    }, 0)
  }

  private scheduleTask(callback: () => void, delay = 0) {
    const timeout = setTimeout(() => {
      this.timeouts.delete(timeout)
      if (this.destroyed) {
        return
      }
      callback()
    }, delay)
    this.timeouts.add(timeout)
  }

  private clearScheduledTasks() {
    this.timeouts.forEach((timeout) => clearTimeout(timeout))
    this.timeouts.clear()
  }

  public updateFileCache(newlyLoadedFiles: UploadedFiles) {
    // Loop through newly loaded data
    newlyLoadedFiles.forEach((uploadedFile) => {
      // Find existing record for each of the loaded uploadedFiles
      const staleFile = this.uploadedFiles.find((r) => r.id === uploadedFile.id)

      this.scheduleTask(() => {
        if (this.destroyed) {
          return
        }
        if (staleFile) {
          // Update only the fields that changed
          Object.keys(uploadedFile).forEach((key) => {
            staleFile[key] = uploadedFile[key]
          })
        } else {
          // Add new repayment to the existing array (in-place)
          this.uploadedFiles.push(uploadedFile)
        }
        this.uploadedFiles = this.sortUploadedFiles([...(this.uploadedFiles || [])])
      })
    })
  }

  private sortUploadedFiles(files: UploadedFiles): UploadedFiles {
    const field = this.sortField || DEFAULT_SORT_FIELD
    const direction = this.sortOrder === 1 ? 1 : -1

    return [...files].sort((a, b) => {
      const aValue = this.getSortableFileValue(a, field)
      const bValue = this.getSortableFileValue(b, field)

      if (aValue === bValue) {
        return 0
      }
      if (aValue === null || aValue === undefined || aValue === '') {
        return 1 * direction
      }
      if (bValue === null || bValue === undefined || bValue === '') {
        return -1 * direction
      }

      if (field === 'date') {
        const aTime = new Date(String(aValue)).getTime()
        const bTime = new Date(String(bValue)).getTime()
        if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
          return (aTime - bTime) * direction
        }
      }

      if (field === 'repayment_count') {
        return (Number(aValue) - Number(bValue)) * direction
      }

      return String(aValue).localeCompare(String(bValue), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction
    })
  }

  private getSortableFileValue(file: UploadedFile, field: string): string | number {
    switch (field) {
      case 'date':
        return file.date || ''
      case 'repayment_count':
        return file.repayment_count ?? 0
      case 'name':
        return file.name || ''
      case 'source':
        return file.source || ''
      case 'created_by':
        return file.created_by || ''
      case 'status':
        return file.status || ''
      default:
        return (file as any)?.[field] ?? ''
    }
  }
}
